"use client";

import { forwardRef, type ButtonHTMLAttributes } from "react";
import { CircleAlert, ExternalLink } from "lucide-react";
import { AeraButton } from "@/app/components/ui/AeraButton";

type ConnectionLinkButtonProps = {
  href?: string;
  onClick?: ButtonHTMLAttributes<HTMLButtonElement>["onClick"];
  ariaLabel: string;
  icon?: "external" | "alert";
  disabled?: boolean;
  ariaExpanded?: boolean;
  onPointerEnter?: ButtonHTMLAttributes<HTMLButtonElement>["onPointerEnter"];
  onPointerLeave?: ButtonHTMLAttributes<HTMLButtonElement>["onPointerLeave"];
};

const linkButtonClassName =
  "shrink-0 text-aera-gray hover:text-aera-charcoal";

function ConnectionLinkIcon({ icon }: { icon: "external" | "alert" }) {
  const Icon = icon === "external" ? ExternalLink : CircleAlert;
  return (
    <Icon className="h-4.5 w-4.5 shrink-0" strokeWidth={2} aria-hidden />
  );
}

export const ConnectionLinkButton = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ConnectionLinkButtonProps &
    Omit<
      ButtonHTMLAttributes<HTMLButtonElement>,
      keyof ConnectionLinkButtonProps | "aria-label"
    >
>(function ConnectionLinkButton(
  {
    href,
    onClick,
    ariaLabel,
    icon = "external",
    disabled,
    ariaExpanded,
    onPointerEnter,
    onPointerLeave,
    type = "button",
    ...rest
  },
  ref,
) {
  const sharedProps = {
    variant: "link" as const,
    fontWeight: "medium" as const,
    paddingSize: "none" as const,
    className: linkButtonClassName,
    "aria-label": ariaLabel,
    icon: <ConnectionLinkIcon icon={icon} />,
  };

  if (href) {
    return (
      <AeraButton
        {...sharedProps}
        ref={ref}
        as="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
      />
    );
  }

  return (
    <AeraButton
      {...sharedProps}
      ref={ref}
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-expanded={ariaExpanded}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      {...rest}
    />
  );
});

ConnectionLinkButton.displayName = "ConnectionLinkButton";
